import { useState, useEffect } from "react";
import api from "../api";
import { Bounce, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "./openPositions.css";

const OpenPositions = () => {
  const [openPositions, setOpenPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    course: "",
    role: "",
  });

  const navigate = useNavigate();
  const [appliedMap, setAppliedMap] = useState({});

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "TRAINER") {
      alert("Accedi come docente per visualizzare le posizioni aperte!");
      navigate("/login");
      localStorage.clear();
    } else {
      fetchOpenPositions();
    }
  }, [navigate]);

  const fetchOpenPositions = async () => {
    try {
      setLoading(true);
      const response = await api.get("/ricerche/posizioni-aperte");

      if (response.status === 200) {
        const positions = response.data;

        const checks = await Promise.all(
          positions.map(async (pos) => {
            try {
              const res = await api.get(`/candidature/check/${pos.id}`);
              return { ricercaId: pos.id, applied: res.data === true };
            } catch (err) {
              return { ricercaId: pos.id, applied: false };
            }
          })
        );

        const map = {};
        checks.forEach(({ ricercaId, applied }) => {
          map[ricercaId] = applied;
        });

        setAppliedMap(map);
        setOpenPositions(positions);
      } else {
        toast.error("Errore durante il recupero delle posizioni aperte!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const applyPosition = async (id) => {
    try {
      setLoading(true);
      const response = await api.post(`/candidature/${id}`);
      if (response.status === 200) {
        toast.success("Applicazione riuscita!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        await fetchOpenPositions();
      } else if (response.status === 404) {
        toast.error("Posizione non trovata!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } else if (response.status === 400) {
        toast.error("Già applicato!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } else if (response.status === 403) {
        toast.error("Docente non è autorizzato a presentare domanda!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } else {
        toast.error("Qualcosa è andato storto!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      course: "",
      role: "",
    });
  };

  const filteredPositions = openPositions.filter((position) => {
    const matchesSearch =
      position.titoloModulo
        ?.toLowerCase()
        .includes(filters.search.toLowerCase()) ||
      position.titoloCorso
        ?.toLowerCase()
        .includes(filters.search.toLowerCase()) ||
      position.mansione?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesCourse =
      !filters.course || position.titoloCorso === filters.course;
    const matchesRole = !filters.role || position.mansione === filters.role;

    return matchesSearch && matchesCourse && matchesRole;
  });

  const uniqueCourses = [
    ...new Set(openPositions.map((pos) => pos.titoloCorso)),
  ];
  const uniqueRoles = [...new Set(openPositions.map((pos) => pos.mansione))];

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  };

  return (
    <div className="open-positions-container">
      {loading && (
        <div className="open-positions-overlay">
          <div className="open-positions-spinner"></div>
        </div>
      )}

      <div className="open-positions-header">
        <div className="open-positions-header-content">
          <div className="open-positions-title-section">
            <h1 className="open-positions-title">
              <i className="fa-solid fa-briefcase"></i>
              Posizioni Aperte
            </h1>
            <p className="open-positions-subtitle">
              Scopri le opportunità di lavoro disponibili e candidati alle
              posizioni che ti interessano
            </p>
          </div>
          <div className="open-positions-stats">
            <div className="open-positions-stat">
              <span className="stat-number">{openPositions.length}</span>
              <span className="stat-label">Posizioni Totali</span>
            </div>
            <div className="open-positions-stat">
              <span className="stat-number">{filteredPositions.length}</span>
              <span className="stat-label">Risultati</span>
            </div>
          </div>
        </div>
      </div>

      <div className="open-positions-content">
        {/* Filters Section */}
        <div className="open-positions-filters">
          <div className="filters-header">
            <h3 className="filters-title">
              <i className="fa-solid fa-filter"></i>
              Filtri
            </h3>
            {(filters.search || filters.course || filters.role) && (
              <button className="clear-filters-btn" onClick={clearFilters}>
                <i className="fa-solid fa-times"></i>
                <span>Pulisci Filtri</span>
              </button>
            )}
          </div>

          <div className="filters-content">
            <div className="filter-group">
              <label className="filter-label">
                <i className="fa-solid fa-search"></i>
                Cerca
              </label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                className="filter-input"
                placeholder="Cerca per titolo, corso o mansione..."
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">
                <i className="fa-solid fa-graduation-cap"></i>
                Corso
              </label>
              <select
                name="course"
                value={filters.course}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">Tutti i corsi</option>
                {uniqueCourses.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">
                <i className="fa-solid fa-user-tie"></i>
                Mansione
              </label>
              <select
                name="role"
                value={filters.role}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">Tutte le mansioni</option>
                {uniqueRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Active Filters */}
          {(filters.search || filters.course || filters.role) && (
            <div className="active-filters">
              <span className="active-filters-label">Filtri attivi:</span>
              {filters.search && (
                <span className="filter-tag">
                  <i className="fa-solid fa-search"></i>
                  {filters.search}
                  <button
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, search: "" }))
                    }
                    className="filter-tag-remove"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </span>
              )}
              {filters.course && (
                <span className="filter-tag">
                  <i className="fa-solid fa-graduation-cap"></i>
                  {filters.course}
                  <button
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, course: "" }))
                    }
                    className="filter-tag-remove"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </span>
              )}
              {filters.role && (
                <span className="filter-tag">
                  <i className="fa-solid fa-user-tie"></i>
                  {filters.role}
                  <button
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, role: "" }))
                    }
                    className="filter-tag-remove"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Positions Grid */}
        <div className="open-positions-grid">
          {filteredPositions.length > 0 ? (
            filteredPositions.map((posizione) => (
              <div className="position-card" key={posizione.id}>
                <div className="position-card-header">
                  <div className="position-card-badge">
                    <i className="fa-solid fa-briefcase"></i>
                    Posizione Aperta
                  </div>
                  {appliedMap[posizione.id] && (
                    <div className="position-card-applied">
                      <i className="fa-solid fa-check-circle"></i>
                      Candidatura Inviata
                    </div>
                  )}
                </div>

                <div className="position-card-content">
                  <h3 className="position-card-title">
                    <i className="fa-solid fa-file-alt"></i>
                    {posizione.titoloModulo}
                  </h3>

                  <div className="position-card-details">
                    <div className="position-detail">
                      <div className="detail-icon">
                        <i className="fa-solid fa-graduation-cap"></i>
                      </div>
                      <div className="detail-content">
                        <span className="detail-label">Corso</span>
                        <span className="detail-value">
                          {posizione.titoloCorso}
                        </span>
                      </div>
                    </div>

                    <div className="position-detail">
                      <div className="detail-icon">
                        <i className="fa-solid fa-user-tie"></i>
                      </div>
                      <div className="detail-content">
                        <span className="detail-label">Mansione</span>
                        <span className="detail-value">
                          {posizione.mansione}
                        </span>
                      </div>
                    </div>

                    <div className="position-detail">
                      <div className="detail-icon">
                        <i className="fa-solid fa-location-dot"></i>
                      </div>
                      <div className="detail-content">
                        <span className="detail-label">Sede</span>
                        <span className="detail-value">{posizione.sede}</span>
                      </div>
                    </div>

                    {posizione.dataCreazione && (
                      <div className="position-detail">
                        <div className="detail-icon">
                          <i className="fa-solid fa-calendar-plus"></i>
                        </div>
                        <div className="detail-content">
                          <span className="detail-label">
                            Data Pubblicazione
                          </span>
                          <span className="detail-value">
                            {formatDate(posizione.dataCreazione)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="position-card-actions">
                  {appliedMap[posizione.id] ? (
                    <button className="position-btn applied" disabled>
                      <i className="fa-solid fa-check"></i>
                      <span>Candidatura Inviata</span>
                    </button>
                  ) : (
                    <button
                      className="position-btn apply"
                      onClick={() => applyPosition(posizione.id)}
                    >
                      <i className="fa-solid fa-paper-plane"></i>
                      <span>Candidati Ora</span>
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="no-positions">
              <div className="no-positions-icon">
                <i className="fa-solid fa-search"></i>
              </div>
              <h3 className="no-positions-title">Nessuna posizione trovata</h3>
              <p className="no-positions-message">
                {openPositions.length === 0
                  ? "Non ci sono posizioni aperte al momento. Torna più tardi!"
                  : "Prova a modificare i filtri per trovare più risultati."}
              </p>
              {openPositions.length > 0 && (
                <button className="clear-filters-btn" onClick={clearFilters}>
                  <i className="fa-solid fa-times"></i>
                  <span>Pulisci Tutti i Filtri</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpenPositions;
